import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import { createHandWithImage } from '../api/hands';
import { ApiError } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

export default function HomePage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const [situationType, setSituationType] = useState<string>('menzen');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const HEIC_TYPES = ['image/heic', 'image/heif'];

  const handleFile = (f: File) => {
    if (!f.type.startsWith('image/')) {
      setError('画像ファイルを選択してください');
      return;
    }
    setFile(f);
    setError(null);
    if (HEIC_TYPES.includes(f.type)) {
      setPreview(null);
      setPreviewUnavailable(true);
    } else {
      setPreview(URL.createObjectURL(f));
      setPreviewUnavailable(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('is_public', 'true');
      if (situationType) fd.append('situation_type', situationType);
      const titleValue = title.trim() || description.trim();
      if (titleValue) fd.append('title', titleValue);
      const hand = await createHandWithImage(fd);
      navigate(`/hands/${hand.id}`);
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 403 && err.message === 'MONTHLY_LIMIT_EXCEEDED') {
        setShowLimitModal(true);
      } else {
        setError(err.message ?? 'アップロードに失敗しました');
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-gray-900">みんなで何切る</h1>
        <p className="text-gray-500">
          麻雀のスクリーンショットをアップロードして<br />
          みんなで投票しよう
        </p>
      </div>

      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors
          ${isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-gray-400'}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          if (!user) { setShowLoginModal(true); return; }
          const f = e.dataTransfer.files[0];
          if (f) handleFile(f);
        }}
      >
        {preview ? (
          <div className="space-y-3">
            <img src={preview} alt="プレビュー" className="max-h-48 mx-auto rounded object-contain" />
            <p className="text-sm text-gray-500">別の画像をクリック/ドロップで変更</p>
          </div>
        ) : previewUnavailable ? (
          <div className="space-y-3">
            <div className="text-4xl">🖼️</div>
            <p className="text-sm font-medium text-gray-700">{file?.name}</p>
            <p className="text-xs text-gray-400">HEIC形式はプレビュー非対応です。アップロード後に表示されます。</p>
            <p className="text-xs text-gray-400">別の画像をクリック/ドロップで変更</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-5xl">🀄</div>
            <div>
              <p className="text-gray-700 font-medium">麻雀のスクリーンショットをアップロード</p>
              <p className="text-gray-400 text-sm mt-1">ドラッグ&ドロップ または クリックして選択</p>
              <p className="text-gray-400 text-xs mt-1">JPEG / PNG / WebP / HEIC / 20MB以下</p>
            </div>
          </div>
        )}
        {user ? (
          <input
            type="file"
            accept="image/*"
            className="absolute inset-0 opacity-0 cursor-pointer"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
        ) : (
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={() => setShowLoginModal(true)}
          />
        )}
      </div>

      {/* Situation form — shown after image is selected */}
      {file && (
        <div className="border rounded-xl p-4 bg-white space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">局面の種類 <span className="text-red-500">*</span></p>
            <div className="flex gap-4">
              {[
                { value: 'menzen', label: '門前' },
                { value: 'fuuro', label: '副露後' },
                { value: 'naki', label: '鳴き判断' },
              ].map(({ value, label }) => (
                <label key={value} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="situation_type"
                    value={value}
                    checked={situationType === value}
                    onChange={() => setSituationType(value)}
                    className="accent-green-600"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">タイトル（任意）</p>
            <input
              type="text"
              className="w-full border rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              placeholder="例: 東1局 手牌13枚、この手どう進める？（100文字以内）"
              maxLength={100}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">説明・聞きたいこと（任意）</p>
            <textarea
              className="w-full border rounded-lg p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-400"
              rows={3}
              placeholder="局面の状況や聞きたいことを書いてください"
              maxLength={200}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {file && (
        <div className="flex justify-end">
          <Button onClick={handleUpload} loading={isUploading}>
            アップロードして投票へ →
          </Button>
        </div>
      )}

      <Modal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} title="ログインが必要です">
        <div className="space-y-4 text-sm text-gray-700">
          <p>投稿するには<strong>Xアカウント</strong>でログインしてください。</p>
          <p className="text-gray-500">ログイン後、そのまま投稿を続けられます。</p>
          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={() => setShowLoginModal(false)}
              className="px-4 py-2 text-gray-500 hover:text-gray-700"
            >
              閉じる
            </button>
            <button
              onClick={login}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium flex items-center gap-2"
            >
              𝕏 でログイン
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showLimitModal} onClose={() => setShowLimitModal(false)} title="月間投稿上限に達しました">
        <div className="space-y-4 text-sm text-gray-700">
          <p>無料プランでは月<strong>5件</strong>まで投稿できます。</p>
          <p>プランをアップグレードすると投稿数の制限なく利用できます。</p>
          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={() => setShowLimitModal(false)}
              className="px-4 py-2 text-gray-500 hover:text-gray-700"
            >
              閉じる
            </button>
            <Link
              to="/pricing"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              onClick={() => setShowLimitModal(false)}
            >
              プランを確認する →
            </Link>
          </div>
        </div>
      </Modal>
    </div>
  );
}
